import { useState, useEffect, useMemo } from 'react'
import { FiDownload, FiFileText, FiCalendar, FiUsers, FiMapPin, FiLayers, FiUser } from 'react-icons/fi'
import * as XLSX from 'xlsx'
import toast from 'react-hot-toast'
import { SkeletonTable } from '../components/SkeletonLoader'
import { API_URL } from '../config/api'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { formatearFecha } from '../utils/fechas'

const ReportesFichajes = () => {
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()

  // Datos maestros
  const [empleados,  setEmpleados]  = useState([])
  const [grupos,     setGrupos]     = useState([])
  const [centros,    setCentros]    = useState([])

  // Filtros
  const [filtroGrupo,     setFiltroGrupo]     = useState('')
  const [filtroCentro,    setFiltroCentro]    = useState('')
  const [filtroEmpleado,  setFiltroEmpleado]  = useState('todos')
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1)
  const [anioSeleccionado,setAnioSeleccionado]= useState(new Date().getFullYear())

  // Datos
  const [fichajes,  setFichajes]  = useState([])
  const [resumen,   setResumen]   = useState(null)
  const [loading,   setLoading]   = useState(false)
  const [generando, setGenerando] = useState(false)

  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaGestionId) {
      const sep = endpoint.includes('?') ? '&' : '?'
      return `${API_URL}${endpoint}${sep}empresa_id=${empresaGestionId}`
    }
    return `${API_URL}${endpoint}`
  }

  // ──────────────────────────────────────────────────
  // Cargar datos maestros al montar
  // ──────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token')
    const h = { 'Authorization': `Bearer ${token}` }
    Promise.all([
      fetch(buildApiUrl('/empleados'), { headers: h }).then(r => r.ok ? r.json() : { empleados: [] }),
      fetch(buildApiUrl('/grupos-empleados'), { headers: h }).then(r => r.ok ? r.json() : []),
      fetch(buildApiUrl('/centros-trabajo'), { headers: h }).then(r => r.ok ? r.json() : []),
    ]).then(([empData, grpData, ctData]) => {
      setEmpleados(empData.empleados || [])
      setGrupos(Array.isArray(grpData) ? grpData : grpData.grupos || [])
      setCentros(Array.isArray(ctData) ? ctData : ctData.centros || [])
    }).catch(() => {})
  }, [empresaGestionId])

  // ──────────────────────────────────────────────────
  // Empleados filtrados según grupo/centro seleccionado
  // ──────────────────────────────────────────────────
  const empleadosFiltrados = useMemo(() => {
    let lista = empleados
    if (filtroGrupo) {
      lista = lista.filter(e => String(e.grupo_id) === filtroGrupo)
    }
    // Centro: los empleados no tienen centro_trabajo_id directo, filtramos por el centro
    // asignado al grupo si el grupo tiene centro_id, o a través del calendario
    // Por ahora el filtro de centro filtra grupos que pertenecen a ese centro
    if (filtroCentro) {
      const gruposDeCentro = grupos
        .filter(g => String(g.centro_id || g.centro_trabajo_id) === filtroCentro)
        .map(g => String(g.id))
      lista = lista.filter(e => gruposDeCentro.includes(String(e.grupo_id)))
    }
    return lista
  }, [empleados, grupos, filtroGrupo, filtroCentro])

  // Cuando cambia el filtro, si el empleado seleccionado ya no aparece → reset
  useEffect(() => {
    if (filtroEmpleado !== 'todos') {
      const sigueDisponible = empleadosFiltrados.some(e => String(e.id) === filtroEmpleado)
      if (!sigueDisponible) setFiltroEmpleado('todos')
    }
  }, [empleadosFiltrados])

  // ──────────────────────────────────────────────────
  // Cargar fichajes automáticamente al cambiar filtros
  // ──────────────────────────────────────────────────
  useEffect(() => {
    cargarDatos()
  }, [filtroEmpleado, mesSeleccionado, anioSeleccionado, filtroGrupo, filtroCentro])

  const cargarDatos = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      // Si hay filtro de grupo/centro pero empleado = todos, traemos todos y filtramos en client
      const endpoint = filtroEmpleado !== 'todos'
        ? `/fichajes/todos?empleado_id=${filtroEmpleado}&mes=${mesSeleccionado}&anio=${anioSeleccionado}`
        : `/fichajes/todos?mes=${mesSeleccionado}&anio=${anioSeleccionado}`

      const res = await fetch(buildApiUrl(endpoint), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        let data = await res.json()

        // Filtrar por empleados que cumplen los filtros de grupo/centro
        if (filtroEmpleado === 'todos' && empleadosFiltrados.length < empleados.length) {
          const ids = new Set(empleadosFiltrados.map(e => e.id))
          data = data.filter(f => ids.has(f.empleado_id))
        }

        setFichajes(data)
        calcularResumen(data)
      }
    } catch {
      toast.error('Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }

  const calcularResumen = (data) => {
    const completados = data.filter(f => f.hora_salida && f.tiempo_total)
    const dias = new Set(completados.map(f => `${f.empleado_id}_${f.fecha}`)).size
    const totalMin = completados.reduce((s, f) => s + (f.tiempo_total || 0), 0)
    const pausasMin = completados.reduce((s, f) => s + (f.tiempo_pausas || 0), 0)
    const efectivoMin = totalMin - pausasMin
    const fmtH = (m) => `${Math.floor(m/60)}:${String(m%60).padStart(2,'0')}`
    setResumen({
      dias,
      total:    fmtH(totalMin),
      pausas:   fmtH(pausasMin),
      efectivo: fmtH(efectivoMin),
      sin_salida: data.filter(f => !f.hora_salida).length
    })
  }

  // ──────────────────────────────────────────────────
  // Exportar Excel con SheetJS
  // ──────────────────────────────────────────────────
  const exportarExcel = () => {
    try {
      const wb = XLSX.utils.book_new()
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
      const nombreMes = meses[mesSeleccionado - 1]

      const fmtH = (m) => { if (!m) return '0:00'; return `${Math.floor(m/60)}:${String(m%60).padStart(2,'0')}` }

      // ── Hoja 1: Detalle de fichajes ──
      const filas = fichajes.map(f => ({
        'Empleado':        f.empleado_nombre || '-',
        'DNI/NIF':         f.empleado_dni || '-',
        'Fecha':           f.fecha,
        'Día semana':      new Date(f.fecha + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long' }),
        'Hora entrada':    f.hora_entrada || '-',
        'Hora salida':     f.hora_salida || 'Sin fichar',
        'Total (h:m)':     fmtH(f.tiempo_total),
        'Pausas (h:m)':    fmtH(f.tiempo_pausas),
        'Efectivo (h:m)':  fmtH(f.tiempo_efectivo || (f.tiempo_total - (f.tiempo_pausas||0))),
        'Estado':          f.hora_salida ? 'Completo' : 'Incompleto',
      }))

      const ws1 = XLSX.utils.json_to_sheet(filas)

      // Ancho de columnas
      ws1['!cols'] = [
        { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 14 }, { wch: 11 }
      ]
      XLSX.utils.book_append_sheet(wb, ws1, 'Detalle fichajes')

      // ── Hoja 2: Resumen por empleado ──
      const porEmpleado = {}
      fichajes.forEach(f => {
        if (!porEmpleado[f.empleado_nombre]) {
          porEmpleado[f.empleado_nombre] = { nombre: f.empleado_nombre, dias: new Set(), totalMin: 0, pausasMin: 0, sinSalida: 0 }
        }
        const e = porEmpleado[f.empleado_nombre]
        if (f.hora_salida) e.dias.add(f.fecha)
        e.totalMin += f.tiempo_total || 0
        e.pausasMin += f.tiempo_pausas || 0
        if (!f.hora_salida) e.sinSalida++
      })

      const resumenRows = Object.values(porEmpleado).map(e => ({
        'Empleado':            e.nombre,
        'Días trabajados':     e.dias.size,
        'Horas totales':       fmtH(e.totalMin),
        'Horas en pausas':     fmtH(e.pausasMin),
        'Horas efectivas':     fmtH(e.totalMin - e.pausasMin),
        'Fichajes incompletos': e.sinSalida,
      }))

      const ws2 = XLSX.utils.json_to_sheet(resumenRows)
      ws2['!cols'] = [{ wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 20 }]
      XLSX.utils.book_append_sheet(wb, ws2, 'Resumen por empleado')

      // ── Hoja 3: Portada / metadatos ──
      const meta = [
        ['Reporte de Registro Horario'],
        [],
        ['Período:', `${nombreMes} ${anioSeleccionado}`],
        ['Generado:', new Date().toLocaleString('es-ES')],
        ['Empleados incluidos:', filtroEmpleado === 'todos' ? 'Todos' : (empleados.find(e => String(e.id) === filtroEmpleado)?.nombre || filtroEmpleado)],
        filtroGrupo ? ['Grupo:', grupos.find(g => String(g.id) === filtroGrupo)?.nombre || filtroGrupo] : null,
        filtroCentro ? ['Centro:', centros.find(c => String(c.id) === filtroCentro)?.nombre || filtroCentro] : null,
        [],
        ['Total fichajes:', fichajes.length],
        ['Días trabajados:', resumen?.dias || 0],
        ['Horas totales:', resumen?.total || '0:00'],
        ['Horas efectivas:', resumen?.efectivo || '0:00'],
        ['Fichajes sin salida:', resumen?.sin_salida || 0],
        [],
        ['Normativa:', 'Conforme al RD-Ley 8/2019 sobre registro de jornada laboral'],
        ['Conservación:', 'Este documento debe conservarse durante 4 años'],
      ].filter(Boolean)

      const ws3 = XLSX.utils.aoa_to_sheet(meta)
      ws3['!cols'] = [{ wch: 25 }, { wch: 35 }]
      XLSX.utils.book_append_sheet(wb, ws3, 'Información')

      // Reordenar: Información primero
      wb.SheetNames = ['Información', 'Detalle fichajes', 'Resumen por empleado']

      const nombreArchivo = `Fichajes_${nombreMes}_${anioSeleccionado}${filtroEmpleado !== 'todos' ? '_' + (empleados.find(e => String(e.id) === filtroEmpleado)?.nombre?.replace(/\s+/g,'_') || '') : ''}.xlsx`
      XLSX.writeFile(wb, nombreArchivo)
      toast.success('Excel generado correctamente')
    } catch (err) {
      console.error(err)
      toast.error('Error al generar Excel')
    }
  }

  // ──────────────────────────────────────────────────
  // Exportar PDF (backend)
  // ──────────────────────────────────────────────────
  const exportarPDF = async () => {
    setGenerando(true)
    try {
      const token = localStorage.getItem('token')
      const empId = filtroEmpleado === 'todos' ? 'todos' : filtroEmpleado
      const url = `${API_URL}/fichajes/exportar-pdf?empleado_id=${empId}&mes=${mesSeleccionado}&anio=${anioSeleccionado}`
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })

      if (res.ok) {
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = window.URL.createObjectURL(blob)
        const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
        a.download = `Fichajes_${meses[mesSeleccionado-1]}_${anioSeleccionado}${empId !== 'todos' ? '_' + (empleados.find(e => String(e.id) === empId)?.nombre?.replace(/\s+/g,'_') || '') : ''}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(a.href)
        toast.success('PDF generado correctamente')
      } else {
        const err = await res.json()
        toast.error(err.error || 'Error al generar PDF')
      }
    } catch {
      toast.error('Error al generar PDF')
    } finally {
      setGenerando(false)
    }
  }

  const fmtDur   = (m) => { if (!m) return '-'; return `${Math.floor(m/60)}h ${m%60}m` }

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
  const anios  = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Reportes de Fichajes</h2>
        <p className="mt-1 text-sm text-gray-600">Genera y exporta reportes de fichajes en Excel o PDF</p>
      </div>

      {/* ── Filtros ── */}
      <div className="card-glass mb-6">
        <div className="flex flex-wrap items-center gap-3">

          {/* Mes */}
          <div className="flex items-center gap-1.5">
            <FiCalendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <label className="text-xs text-gray-500 whitespace-nowrap">Mes</label>
            <select value={mesSeleccionado} onChange={e => setMesSeleccionado(parseInt(e.target.value))} className="input-glass text-sm py-1.5">
              {meses.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>

          {/* Año */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">Año</label>
            <select value={anioSeleccionado} onChange={e => setAnioSeleccionado(parseInt(e.target.value))} className="input-glass text-sm py-1.5">
              {anios.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className="h-5 w-px bg-gray-200 mx-1 hidden sm:block" />

          {/* Centro */}
          {centros.length > 0 && (
            <div className="flex items-center gap-1.5">
              <FiMapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <label className="text-xs text-gray-500 whitespace-nowrap">Centro</label>
              <select
                value={filtroCentro}
                onChange={e => { setFiltroCentro(e.target.value); setFiltroGrupo(''); setFiltroEmpleado('todos') }}
                className="input-glass text-sm py-1.5"
              >
                <option value="">Todos</option>
                {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}

          {/* Grupo */}
          {grupos.length > 0 && (
            <div className="flex items-center gap-1.5">
              <FiLayers className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <label className="text-xs text-gray-500 whitespace-nowrap">Grupo</label>
              <select
                value={filtroGrupo}
                onChange={e => { setFiltroGrupo(e.target.value); setFiltroEmpleado('todos') }}
                className="input-glass text-sm py-1.5"
              >
                <option value="">Todos</option>
                {grupos
                  .filter(g => !filtroCentro || String(g.centro_id || g.centro_trabajo_id) === filtroCentro)
                  .map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
              </select>
            </div>
          )}

          <div className="h-5 w-px bg-gray-200 mx-1 hidden sm:block" />

          {/* Empleado */}
          <div className="flex items-center gap-1.5">
            <FiUser className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <label className="text-xs text-gray-500 whitespace-nowrap">Empleado</label>
            <select
              value={filtroEmpleado}
              onChange={e => setFiltroEmpleado(e.target.value)}
              className="input-glass text-sm py-1.5"
            >
              <option value="todos">
                {empleadosFiltrados.length < empleados.length
                  ? `Todos (${empleadosFiltrados.length})`
                  : 'Todos'}
              </option>
              {empleadosFiltrados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          </div>

          {(filtroGrupo || filtroCentro) && (
            <button
              onClick={() => { setFiltroCentro(''); setFiltroGrupo(''); setFiltroEmpleado('todos') }}
              className="text-xs text-blue-600 hover:text-blue-800 underline ml-1"
            >
              × Quitar filtros
            </button>
          )}
        </div>
      </div>

      {/* ── KPIs ── */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Días trabajados',      value: resumen.dias,         color: 'from-blue-50 to-blue-100',     text: 'from-blue-600 to-blue-800' },
            { label: 'Horas totales',         value: resumen.total,        color: 'from-green-50 to-green-100',   text: 'from-green-600 to-green-800' },
            { label: 'Horas en pausas',       value: resumen.pausas,       color: 'from-yellow-50 to-yellow-100', text: 'from-yellow-600 to-yellow-800' },
            { label: 'Horas efectivas',       value: resumen.efectivo,     color: 'from-purple-50 to-purple-100', text: 'from-purple-600 to-purple-800' },
            { label: 'Sin hora de salida',    value: resumen.sin_salida,   color: resumen.sin_salida > 0 ? 'from-red-50 to-red-100' : 'from-gray-50 to-gray-100', text: resumen.sin_salida > 0 ? 'from-red-600 to-red-800' : 'from-gray-400 to-gray-600' },
          ].map(kpi => (
            <div key={kpi.label} className={`card-elevated bg-gradient-to-br ${kpi.color} p-4`}>
              <div className="text-xs text-gray-600 font-medium">{kpi.label}</div>
              <div className={`text-xl font-bold bg-gradient-to-r ${kpi.text} bg-clip-text text-transparent mt-1`}>
                {kpi.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Botones exportación ── */}
      {fichajes.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={exportarExcel}
            disabled={generando}
            className="btn btn-primary btn-ripple"
          >
            <FiDownload className="mr-2 h-4 w-4" />
            Exportar Excel
          </button>
          <button
            onClick={exportarPDF}
            disabled={generando}
            className="btn btn-secondary"
          >
            <FiFileText className="mr-2 h-4 w-4" />
            {generando ? 'Generando PDF...' : 'Exportar PDF'}
          </button>
          <span className="self-center text-xs text-gray-400">
            {fichajes.length} registro{fichajes.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* ── Tabla ── */}
      {loading ? (
        <SkeletonTable rows={6} cols={7} />
      ) : fichajes.length > 0 ? (
        <div className="card-glass overflow-hidden">
          <div className="px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200/50">
            <h3 className="text-sm font-medium text-gray-700">Vista previa</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  {['Empleado','Fecha','Entrada','Salida','Total','Pausas','Efectivo'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fichajes.map(f => (
                  <tr key={f.id} className={!f.hora_salida ? 'bg-red-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{f.empleado_nombre || '-'}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <FiCalendar className="h-3.5 w-3.5 text-gray-400" />
                        {formatearFecha(f.fecha)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{f.hora_entrada || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {f.hora_salida
                        ? <span className="text-gray-900">{f.hora_salida}</span>
                        : <span className="text-red-600 font-medium text-xs bg-red-100 px-2 py-0.5 rounded-full">Sin fichar</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{fmtDur(f.tiempo_total)}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDur(f.tiempo_pausas)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                      {fmtDur(f.tiempo_efectivo || (f.tiempo_total ? f.tiempo_total - (f.tiempo_pausas||0) : null))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : !loading ? (
        <div className="card-glass text-center py-14">
          <FiCalendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No hay fichajes para el período y filtros seleccionados</p>
        </div>
      ) : null}
    </div>
  )
}

export default ReportesFichajes
